import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const OurPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    image: '',
    web_link:''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'our_partners_V2'); // Fetch partners data
        if (response.success) {
          setPartners(response.data);
        } else {
          console.error(response.message || "Failed to load partners data");
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      image: '',
      web_link: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (partnerItem) => {
    setSelectedPartner(partnerItem);
    setFormData({
      name: partnerItem.name || '',
      startDate: partnerItem.startDate || '',
      endDate: partnerItem.endDate || '',
      image: partnerItem.image || '',
      web_link: partnerItem.web_link || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (partnerItem) => {
    setPartnerToDelete(partnerItem);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'our_partners_V2', partnerToDelete.id); // Delete partner by ID
      if (response.success) {
        toast.success(`Partner deleted successfully`);
        setPartners(partners.filter(item => item.id !== partnerToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete partner");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete partner");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setPartnerToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;

      if (selectedPartner) {
        // Update existing partner
        response = await updateData('proccess.php', 'our_partners_V2', formData, selectedPartner.id); // Update partner
        if (response.success) {
          toast.success(`Partner updated successfully`);
          setPartners(response.data);
        }
      } else {
        // Add new partner
        response = await addData('proccess.php', 'our_partners_V2', formData); // Add partner
        if (response.success) {
          toast.success(`Partner added successfully`);
          // Assuming response.data is either the new partner or the updated list
            setPartners(response.data);
          
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedPartner(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "name", headerName: "Partner Name", flex: 1, minWidth: 120 },
    { field: "startDate", headerName: "Start Date", flex: 1, minWidth: 120 },
    { field: "endDate", headerName: "End Date", flex: 1, minWidth: 120 },
    {
      field: "image",
      headerName: "Partner Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Partner"}
              style={{ width: 50, height: 50, objectFit: "cover" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    {
      field: "web_link",
      headerName: "Web Link",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <a 
              style={{cursor: 'pointer', textDecoration: 'underline', color: '#00ff40'}}
              onClick={() => window.open(`${params.value}`, "_blank")}
            >Open Web Link</a>
          ) : (
            "No Link"
          )}
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No partners available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedPartner(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedPartner ? "Edit Partner" : "Add New Partner"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                style={{ width: "100%" }}
                id="name"
                name="name"
                label="Partner Name"
                variant="standard"
                value={formData.name}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="startDate"
                name="startDate"
                label="Start Date"
                type="date"
                variant="standard"
                value={formData.startDate}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="endDate"
                name="endDate"
                label="End Date"
                type="date"
                variant="standard"
                value={formData.endDate}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
              />
              <Box width={"100%"}>
                <label>Partner Image</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedPartner}
                />
              </Box>
              <TextField
                style={{ width: "100%" }}
                id="web_link"
                name="web_link"
                label="Partner Web Link"
                variant="standard"
                value={formData.web_link}
                onChange={handleFormChange}
                required
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedPartner(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedPartner ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{partnerToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Partners Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage partners
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Partner
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "45vh", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={partners}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default OurPartners;